import { useEffect, useRef } from 'react';
import type { Article } from '@/types/article';

// Helpers shared by the new-design Content hub and article pages.

export const ARTICLE_FIELDS = 'id, slug, category, category_color, read_time, title, excerpt, author, author_role, published_date, image, body, status';

export function formatDate(iso: string, lang: string) {
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (Number.isNaN(d.getTime())) return iso;
  const locale = lang === 'ko' ? 'ko-KR' : lang === 'bn' ? 'bn-BD' : 'en-GB';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

export const initials = (name: string | null) =>
  (name || 'Rx.K Seoul')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

// Adds the "in" class to every .rv element once it scrolls into view; re-scans on each render.
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const io = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    io.current = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.current?.unobserve(e.target);
          }
        }),
      { threshold: 0.1 },
    );
    const el = ref.current;
    return () => {
      io.current?.disconnect();
      el?.querySelectorAll('[data-io]').forEach((n) => n.removeAttribute('data-io'));
    };
  }, []);
  useEffect(() => {
    ref.current?.querySelectorAll('.rv:not(.in):not([data-io])').forEach((el) => {
      el.setAttribute('data-io', '');
      io.current?.observe(el);
    });
  });
  return ref;
}

export function CategoryTag({ article }: { article: Pick<Article, 'category' | 'category_color'> }) {
  return (
    <span className="ctag" style={{ ['--c' as string]: article.category_color || '#8a1c2b' }}>
      <i />
      {article.category}
    </span>
  );
}

export const ARROW = 'M5 12h14M13 6l6 6-6 6';
